import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../hooks/useAuthStore';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  Shield,
  BarChart3,
  Users,
  AlertCircle,
  Sparkles,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getErrorCode, getApiErrorMessage } from '@/utils/apiError';
import { authApi } from '@/services/api';
import { BaytarcLogo } from '@/components/brand/BaytarcLogo';
import type { LoginRequest, OrgOption } from '../types/auth';

const FEATURES = [
  {
    icon: Building2,
    title: 'Full Portfolio Management',
    desc: 'Manage properties, units, and tenants from a single dashboard.',
  },
  {
    icon: BarChart3,
    title: 'Financial Reporting & Analytics',
    desc: 'Track income, expenses, and KPIs across your entire portfolio.',
  },
  {
    icon: Users,
    title: 'Tenant & Lease Lifecycle',
    desc: 'Streamline KYC onboarding, lease renewals, and maintenance.',
  },
  {
    icon: Shield,
    title: 'Enterprise-Grade Security',
    desc: 'Role-based access control, 2FA, and full audit trails.',
  },
];

const STATS = [
  { value: '250+', label: 'Properties managed' },
  { value: '99.99%', label: 'Uptime SLA' },
  { value: '10+', label: 'Organisations' },
];

type LoginStep = 'email' | 'credentials' | '2fa';

// Shared input style applied inline to match existing design
const inputBase: React.CSSProperties = {
  border: '1.5px solid #e2e8f0',
  background: '#ffffff',
  color: '#101828',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};
const inputFocus: React.CSSProperties = {
  borderColor: '#c10007',
  boxShadow: '0 0 0 4px rgba(193,0,7,0.08), 0 1px 2px rgba(0,0,0,0.04)',
};

const LoginPage = () => {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState<'totp' | 'email'>('totp');
  const [resendLoading, setResendLoading] = useState(false);

  const navigate = useNavigate();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();

  // Redirect already-authenticated users (e.g. storageState pre-auth in E2E)
  // to "/" so RootRedirect can apply role-based landing logic (e.g. OWNER/
  // LANDLORD -> /owner-portal) instead of hardcoding /dashboard for everyone.
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailLoading(true);
    try {
      const result = await authApi.checkEmail(trimmed);
      setOrgs(result.orgs);
      // Auto-select if only one org (or none, for accounts without org)
      if (result.orgs.length === 1) {
        setSelectedOrgId(result.orgs[0].id);
      } else {
        setSelectedOrgId(null);
      }
      setStep('credentials');
    } catch {
      setEmailError('Unable to look up your account. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (!password) {
      toast.error('Password is required');
      return;
    }
    if (orgs.length > 1 && !selectedOrgId) {
      toast.error('Please select your organisation');
      return;
    }

    try {
      const loginData: LoginRequest = {
        email: email.trim(),
        password,
        ...(selectedOrgId ? { organisationId: selectedOrgId } : {}),
      };
      await login(loginData);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err: unknown) {
      const code = getErrorCode(err);
      const message = getApiErrorMessage(err, 'Login failed');
      if (code === 'TWO_FACTOR_REQUIRED') {
        setTwoFAMethod('totp');
        setStep('2fa');
      } else if (code === 'TWO_FACTOR_EMAIL_REQUIRED') {
        setTwoFAMethod('email');
        setStep('2fa');
      } else if (message.toLowerCase().includes('device')) {
        toast.error(
          'New device detected. Please check your email for an approval link, or contact your administrator.',
          { duration: 8000 }
        );
      } else if (code === 'UnauthorizedException' && message.includes('New device detected')) {
        toast.error(
          'New device detected. Please check your email for an approval link, or contact your administrator.',
          { duration: 8000 }
        );
      } else {
        toast.error(message);
      }
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      const loginData: LoginRequest = {
        email: email.trim(),
        password,
        ...(twoFAMethod === 'totp' ? { twoFACode } : { emailOtpCode: twoFACode }),
        ...(selectedOrgId ? { organisationId: selectedOrgId } : {}),
      };
      await login(loginData);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Login failed');
      toast.error(message);
    }
  };

  const handleResendEmailOtp = async () => {
    setResendLoading(true);
    try {
      // Re-trigger by attempting login without a code, backend will resend and return the error
      await login({
        email: email.trim(),
        password,
        ...(selectedOrgId ? { organisationId: selectedOrgId } : {}),
      });
    } catch (err: unknown) {
      const code = getErrorCode(err);
      if (code === 'TWO_FACTOR_EMAIL_REQUIRED') {
        toast.success('A new code has been sent to your email.');
        setTwoFACode('');
      } else {
        toast.error(getApiErrorMessage(err, 'Failed to resend code'));
      }
    } finally {
      setResendLoading(false);
    }
  };

  const goBack = () => {
    clearError();
    if (step === '2fa') {
      setTwoFACode('');
      setStep('credentials');
    } else if (step === 'credentials') {
      setPassword('');
      setOrgs([]);
      setSelectedOrgId(null);
      setStep('email');
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ── Inline keyframes ── */}
      <style>{`
        @keyframes login-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 15px) scale(0.95); }
        }
        @keyframes login-float-reverse {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-25px, 20px) scale(1.03); }
          66% { transform: translate(15px, -25px) scale(0.97); }
        }
        @keyframes login-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes login-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .login-animate-in {
          animation: login-fade-up 0.6s ease-out both;
        }
        .login-animate-delay-1 { animation-delay: 0.1s; }
        .login-animate-delay-2 { animation-delay: 0.2s; }
        .login-animate-delay-3 { animation-delay: 0.3s; }
        .login-animate-delay-4 { animation-delay: 0.4s; }
        .login-animate-delay-5 { animation-delay: 0.5s; }
        .login-shimmer-text {
          background: linear-gradient(90deg, #fff 0%, #fca5a5 40%, #fff 60%, #fca5a5 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: login-shimmer 4s linear infinite;
        }
      `}</style>

      {/* ── Left panel ── */}
      <div
        className="hidden lg:flex lg:w-[54%] xl:w-[52%] flex-col justify-between relative overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #080b14 0%, #0f172a 30%, #131c33 55%, #0c1220 100%)',
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute w-[600px] h-[600px] rounded-full opacity-30"
            style={{
              top: '-15%',
              left: '-10%',
              background: 'radial-gradient(circle, rgba(193,0,7,0.25) 0%, transparent 70%)',
              animation: 'login-float 15s ease-in-out infinite',
            }}
          />
          <div
            className="absolute w-[500px] h-[500px] rounded-full opacity-20"
            style={{
              bottom: '-10%',
              right: '-5%',
              background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)',
              animation: 'login-float-reverse 18s ease-in-out infinite',
            }}
          />
          <div
            className="absolute w-[300px] h-[300px] rounded-full opacity-15"
            style={{
              top: '40%',
              right: '15%',
              background: 'radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%)',
              animation: 'login-float 12s ease-in-out infinite',
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[800px] h-[800px] rounded-full border border-white/[0.03]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[600px] h-[600px] rounded-full border border-white/[0.04]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[400px] h-[400px] rounded-full border border-white/[0.05]" />
        </div>

        <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-14">
          <div className="login-animate-in">
            <div className="flex items-center gap-3">
              <BaytarcLogo variant="white" size="lg" />
            </div>
          </div>

          <div className="space-y-10">
            <div className="login-animate-in login-animate-delay-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] mb-6">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-medium text-white/60 tracking-wide uppercase">
                  Trusted by 10+ organisations
                </span>
              </div>
              <h2
                className="text-[2.75rem] xl:text-5xl font-extrabold text-white leading-[1.1] tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Property management
                <br />
                <span className="login-shimmer-text">built for growth.</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed max-w-sm" style={{ color: '#8b9cc0' }}>
                Everything you need to manage your property portfolio from tenant onboarding to
                financial reporting.
              </p>
            </div>

            <div className="space-y-3">
              {FEATURES.map(({ icon: Icon, title, desc }, i) => (
                <div
                  key={title}
                  className={`login-animate-in login-animate-delay-${i + 2} group flex gap-4 p-4 -mx-2 rounded-2xl transition-all duration-300 cursor-default`}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(193,0,7,0.15) 0%, rgba(193,0,7,0.05) 100%)',
                      border: '1px solid rgba(193,0,7,0.2)',
                    }}
                  >
                    <Icon className="w-[18px] h-[18px]" style={{ color: '#f87171' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white/90 tracking-tight">{title}</p>
                    <p className="text-[13px] mt-1 leading-relaxed" style={{ color: '#6b7fa3' }}>
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div
              className="login-animate-in login-animate-delay-5 flex items-center gap-0 rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {STATS.map(({ value, label }, i) => (
                <div
                  key={label}
                  className="flex-1 text-center py-4 px-3"
                  style={{
                    borderRight: i < STATS.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  }}
                >
                  <p
                    className="text-xl xl:text-2xl font-bold text-white tracking-tight"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {value}
                  </p>
                  <p
                    className="text-[11px] font-medium mt-1 tracking-wide uppercase"
                    style={{ color: '#5a6b88' }}
                  >
                    {label}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[13px]" style={{ color: '#3d4b63' }}>
              © {new Date().getFullYear()} Baytarc. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right panel: login form ── */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-14 xl:px-20 relative"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fc 40%, #f1f3f9 100%)',
        }}
      >
        <div className="absolute top-0 right-0 w-80 h-80 opacity-30 pointer-events-none">
          <div
            className="w-full h-full rounded-full"
            style={{
              background:
                'radial-gradient(circle at center, rgba(193,0,7,0.06) 0%, transparent 70%)',
              transform: 'translate(30%, -30%)',
            }}
          />
        </div>

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <BaytarcLogo variant="navy" size="lg" />
        </div>

        <div className="w-full max-w-[380px] relative z-10">
          {/* ── Step heading ── */}
          {step === '2fa' ? (
            <div className="mb-7 login-animate-in">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5"
                style={{
                  background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                  boxShadow: '0 4px 12px rgba(193,0,7,0.08)',
                }}
              >
                {twoFAMethod === 'email' ? (
                  <Mail className="w-7 h-7" style={{ color: '#a10006' }} />
                ) : (
                  <Shield className="w-7 h-7" style={{ color: '#a10006' }} />
                )}
              </div>
              <h1
                className="text-[28px] font-bold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", color: '#101828' }}
              >
                {twoFAMethod === 'email' ? 'Check your email' : 'Two-step verification'}
              </h1>
              <p className="mt-2 text-[15px] leading-relaxed" style={{ color: '#64748b' }}>
                {twoFAMethod === 'email'
                  ? `We sent a 6-digit verification code to ${email.trim()}. Enter it below to sign in.`
                  : 'Enter the 6-digit code from your authenticator app to continue.'}
              </p>
            </div>
          ) : step === 'credentials' ? (
            <div className="mb-7 login-animate-in">
              <h1
                className="text-[28px] font-bold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", color: '#101828' }}
              >
                {orgs.length > 1 ? 'Select organisation' : 'Welcome back'}
              </h1>
              <p className="mt-2 text-[15px]" style={{ color: '#64748b' }}>
                {orgs.length > 1
                  ? 'You have accounts in multiple organisations. Select one and enter your password.'
                  : `Signing in as ${email.trim()}`}
              </p>
            </div>
          ) : (
            <div className="mb-7 login-animate-in">
              <h1
                className="text-[28px] font-bold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", color: '#101828' }}
              >
                Welcome back
              </h1>
              <p className="mt-2 text-[15px]" style={{ color: '#64748b' }}>
                Enter your email address to continue.
              </p>
            </div>
          )}

          {/* ── Step 1: Email ── */}
          {step === 'email' && (
            <form onSubmit={handleEmailContinue} className="space-y-5">
              <div className="login-animate-in login-animate-delay-1">
                <label className="block text-sm font-medium mb-2" style={{ color: '#344054' }}>
                  Email address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none"
                    style={{ color: '#94a3b8' }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                    required
                    className="w-full py-3 pl-11 pr-4 rounded-xl text-sm transition-all duration-200 outline-none"
                    style={inputBase}
                    onFocus={(e) => Object.assign(e.currentTarget.style, inputFocus)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, inputBase)}
                  />
                </div>
                {emailError && (
                  <p
                    className="mt-2 text-xs flex items-center gap-1.5"
                    style={{ color: '#dc2626' }}
                  >
                    <AlertCircle className="w-3.5 h-3.5" />
                    {emailError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={emailLoading}
                className="login-animate-in login-animate-delay-2 w-full py-3 px-4 rounded-xl text-white text-sm font-semibold
                  flex items-center justify-center gap-2 transition-all duration-200
                  disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.99]"
                style={{
                  background: 'linear-gradient(135deg, #c10007 0%, #a10006 50%, #810005 100%)',
                  boxShadow: '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  if (!emailLoading) {
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(193,0,7,0.35), 0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {emailLoading ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full animate-spin"
                      style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                    />
                    Checking…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <div className="login-animate-in login-animate-delay-3 flex items-center gap-4 my-6">
                <div
                  className="flex-1 h-px"
                  style={{ background: 'linear-gradient(to right, transparent, #e2e8f0)' }}
                />
                <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>
                  or
                </span>
                <div
                  className="flex-1 h-px"
                  style={{ background: 'linear-gradient(to left, transparent, #e2e8f0)' }}
                />
              </div>

              <p
                className="login-animate-in login-animate-delay-4 text-center text-sm"
                style={{ color: '#64748b' }}
              >
                New organisation?{' '}
                <Link
                  to="/register"
                  className="font-semibold transition-colors duration-200"
                  style={{ color: '#a10006' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#810005')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#a10006')}
                >
                  Register here →
                </Link>
              </p>

              <p className="login-animate-in login-animate-delay-5 text-center text-sm mt-2">
                <Link
                  to="/forgot-password"
                  className="font-medium transition-colors duration-200"
                  style={{ color: '#64748b' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#1e293b')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                >
                  Forgot password?
                </Link>
              </p>
            </form>
          )}

          {/* ── Step 2: Org (if multiple) + Password ── */}
          {step === 'credentials' && (
            <form onSubmit={handleCredentialsSubmit} className="space-y-5">
              {/* Org selector, hidden when only one org */}
              {orgs.length > 1 && (
                <div className="login-animate-in login-animate-delay-1">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#344054' }}>
                    Organisation
                  </label>
                  <div className="relative">
                    <Building2
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none"
                      style={{ color: '#94a3b8' }}
                    />
                    <select
                      value={selectedOrgId ?? ''}
                      onChange={(e) => setSelectedOrgId(Number(e.target.value) || null)}
                      required
                      className="w-full py-3 pl-11 pr-10 rounded-xl text-sm transition-all duration-200 outline-none appearance-none"
                      style={inputBase}
                      onFocus={(e) => Object.assign(e.currentTarget.style, inputFocus)}
                      onBlur={(e) => Object.assign(e.currentTarget.style, inputBase)}
                    >
                      <option value="">, Select organisation,</option>
                      {orgs.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                      style={{ color: '#94a3b8' }}
                    />
                  </div>
                </div>
              )}

              {/* Show which email / org we're logging into */}
              {orgs.length === 1 && (
                <div
                  className="login-animate-in flex items-center gap-2 px-3 py-2 rounded-xl"
                  style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}
                >
                  <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: '#64748b' }} />
                  <span className="text-sm font-medium" style={{ color: '#334155' }}>
                    {orgs[0].name}
                  </span>
                </div>
              )}

              {/* Password */}
              <div className="login-animate-in login-animate-delay-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium" style={{ color: '#344054' }}>
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium transition-colors duration-200"
                    style={{ color: '#a10006' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#810005')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#a10006')}
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none"
                    style={{ color: '#94a3b8' }}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    autoFocus
                    required
                    className="w-full py-3 pl-11 pr-12 rounded-xl text-sm transition-all duration-200 outline-none"
                    style={inputBase}
                    onFocus={(e) => Object.assign(e.currentTarget.style, inputFocus)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, inputBase)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors duration-200"
                    style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#475569')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-[18px] h-[18px]" />
                    ) : (
                      <Eye className="w-[18px] h-[18px]" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <label className="login-animate-in login-animate-delay-3 flex items-center gap-3 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className="w-[18px] h-[18px] rounded-md transition-all duration-200"
                    style={{
                      border: '1.5px solid #cbd5e1',
                      background: rememberMe
                        ? 'linear-gradient(135deg, #c10007, #a10006)'
                        : '#ffffff',
                    }}
                  >
                    {rememberMe && (
                      <svg className="w-full h-full p-0.5" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M3 6l2 2 4-4"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm" style={{ color: '#475569' }}>
                  Remember me for 30 days
                </span>
              </label>

              {/* API error */}
              {error && (
                <div
                  className="flex items-start gap-3 p-4 rounded-xl login-animate-in"
                  style={{
                    background: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
                    border: '1px solid #fecaca',
                  }}
                >
                  <AlertCircle
                    className="flex-shrink-0 w-[18px] h-[18px] mt-0.5"
                    style={{ color: '#dc2626' }}
                  />
                  <p className="text-sm leading-snug" style={{ color: '#991b1b' }}>
                    {error.message}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="login-animate-in login-animate-delay-4 w-full py-3 px-4 rounded-xl text-white text-sm font-semibold
                  flex items-center justify-center gap-2 transition-all duration-200
                  disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.99]"
                style={{
                  background: 'linear-gradient(135deg, #c10007 0%, #a10006 50%, #810005 100%)',
                  boxShadow: '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(193,0,7,0.35), 0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full animate-spin"
                      style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                    />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={goBack}
                className="w-full py-2.5 text-sm transition-colors duration-200"
                style={{ color: '#64748b' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#1e293b')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
              >
                ← Back
              </button>
            </form>
          )}

          {/* ── Step 3: 2FA ── */}
          {step === '2fa' && (
            <form onSubmit={handle2FASubmit} className="space-y-5">
              <div className="login-animate-in">
                <label className="block text-sm font-medium mb-2" style={{ color: '#344054' }}>
                  {twoFAMethod === 'email' ? 'Email verification code' : 'Authentication code'}
                </label>
                <div className="relative">
                  {twoFAMethod === 'email' ? (
                    <Mail
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none"
                      style={{ color: '#94a3b8' }}
                    />
                  ) : (
                    <Shield
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none"
                      style={{ color: '#94a3b8' }}
                    />
                  )}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000 000"
                    maxLength={6}
                    autoFocus
                    required
                    className="w-full py-3 pl-11 pr-4 rounded-xl text-sm tracking-[0.3em] font-mono transition-all duration-200 outline-none"
                    style={inputBase}
                    onFocus={(e) => Object.assign(e.currentTarget.style, inputFocus)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, inputBase)}
                  />
                </div>
                {twoFAMethod === 'email' ? (
                  <p className="mt-2.5 text-xs" style={{ color: '#94a3b8' }}>
                    Didn't receive it?{' '}
                    <button
                      type="button"
                      onClick={handleResendEmailOtp}
                      disabled={resendLoading}
                      className="font-medium transition-colors duration-200 disabled:opacity-50"
                      style={{ color: '#a10006' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#810005')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#a10006')}
                    >
                      {resendLoading ? 'Sending…' : 'Resend code'}
                    </button>
                  </p>
                ) : (
                  <p className="mt-2.5 text-xs" style={{ color: '#94a3b8' }}>
                    Can't access your app?{' '}
                    <button
                      type="button"
                      className="font-medium transition-colors duration-200"
                      style={{ color: '#a10006' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#810005')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#a10006')}
                    >
                      Use a recovery code
                    </button>
                  </p>
                )}
              </div>

              {error && (
                <div
                  className="flex items-start gap-3 p-4 rounded-xl login-animate-in"
                  style={{
                    background: 'linear-gradient(135deg, #fef2f2, #fff1f2)',
                    border: '1px solid #fecaca',
                  }}
                >
                  <AlertCircle
                    className="flex-shrink-0 w-[18px] h-[18px] mt-0.5"
                    style={{ color: '#dc2626' }}
                  />
                  <p className="text-sm leading-snug" style={{ color: '#991b1b' }}>
                    {error.message}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-xl text-white text-sm font-semibold
                  flex items-center justify-center gap-2 transition-all duration-200
                  disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.99]"
                style={{
                  background: 'linear-gradient(135deg, #c10007 0%, #a10006 50%, #810005 100%)',
                  boxShadow: '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(193,0,7,0.35), 0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(193,0,7,0.25), 0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full animate-spin"
                      style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }}
                    />
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify & continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={goBack}
                className="w-full py-2.5 text-sm transition-colors duration-200"
                style={{ color: '#64748b' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#1e293b')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
              >
                ← Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
